import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from 'firebase/firestore';
import CreateFactionFlow from '../components/faction/CreateFactionFlow';
import UserProfileCard from '../components/user/UserProfileCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EditProfileForm from '../components/user/EditProfileForm';
import FactionCard from '../components/faction/FactionCard';
import { Offcanvas } from 'react-bootstrap';
import SectionAd from '../components/ads/SectionAd';
import SeasonCreationBanner from '../components/SeasonCreationBanner';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import { mergeFactionWrestlersWithDetails } from '../utils/factionWrestlers';
import {
  calculateFactionTotalScore,
  getFactionMainEventerStatus,
  rosterRequiresLineup,
  shouldBlockFactionScoringForMissingLineup
} from '../utils/factionScoring';
import { canCreateFaction, isFactionCreationSeasonOpen } from '../utils/seasonGate';
import { calculateGmMatchPointsByFaction, getFactionGmMatches, normalizeGmMatch } from '../utils/gmMatches';
import { clearFavoriteFactionSelection, saveFavoriteFactionSelection } from '../utils/favoriteFactionStorage';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';
import { buildFactionLastShowSummary, findLatestScoredShowEntry } from '../utils/lastShowSummary';
import { loadUserFactionAlertState } from '../utils/factionAlerts';

export default function Profile() {
  const navigate = useNavigate();
  const appliedFavoriteDefaultRef = useRef(null);
  const loadingRef = useRef(true);
  const [userProfile, setUserProfile] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [formDataProfile, setFormDataProfile] = useState({
    username: '',
    email: '',
    profileImageUrl: '',
    notificationsEnabled: false
  });

  // Nuovi stati per roster e fazioni
  const [rostersParticipating, setRostersParticipating] = useState([]);
  const [selectedRosterId, setSelectedRosterId] = useState(null);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [factionForRoster, setFactionForRoster] = useState(null);
  const [members, setMembers] = useState([]);
  const [userFactionsMap, setUserFactionsMap] = useState({}); // rosterId -> faction

  // Stati per il flusso di creazione/modifica fazione
  const [showFactionFlow, setShowFactionFlow] = useState(false);
  const [editingFactionId, setEditingFactionId] = useState(null);
  const [skipFactionModal, setSkipFactionModal] = useState(false);
  const [defaultRoster, setDefaultRoster] = useState(null);

  // Stati di caricamento ed errore
  const [loading, setLoading] = useState(true);
  const [profileBootstrapReady, setProfileBootstrapReady] = useState(false);
  const [isRosterContextLoading, setIsRosterContextLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditingFaction, setIsEditingFaction] = useState(false);
  const [formDataFaction, setFormDataFaction] = useState({ name: '', imageUrl: '' });
  const [latestShowEntry, setLatestShowEntry] = useState(null);
  const [pendingRequestsByRosterId, setPendingRequestsByRosterId] = useState({});
  const [factionAlertStateByRosterId, setFactionAlertStateByRosterId] = useState({});

  const buildProfileIdentity = (authUser, profileData = {}) => {
    const resolvedEmail = profileData.email || authUser?.email || '';
    const resolvedUsername =
      profileData.username ||
      authUser?.displayName ||
      (resolvedEmail ? resolvedEmail.split('@')[0] : '');

    return {
      email: resolvedEmail,
      username: resolvedUsername,
      profileImageUrl: profileData.profileImageUrl || '',
      notificationsEnabled: profileData.notificationsEnabled || false
    };
  };

  const buildUserFactionIds = (userData = {}) => (
    Array.from(
      new Set(
        [
          ...(Array.isArray(userData.factionIds) ? userData.factionIds : []),
          userData.factionId,
          userData.favoriteFactionId
        ].filter(Boolean)
      )
    )
  );

  const fetchFactionsByIds = async (factionIds) => {
    if (!Array.isArray(factionIds) || factionIds.length === 0) return [];

    const factionChunks = [];
    for (let index = 0; index < factionIds.length; index += 10) {
      factionChunks.push(factionIds.slice(index, index + 10));
    }

    const snapshots = await Promise.all(
      factionChunks.map((chunk) =>
        getDocs(query(collection(db, 'factions'), where('__name__', 'in', chunk)))
      )
    );

    return snapshots.flatMap((snapshot) =>
      snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
    );
  };

  const fetchRostersByIds = async (rosterIds) => {
    if (!Array.isArray(rosterIds) || rosterIds.length === 0) return [];

    const rosterChunks = [];
    for (let index = 0; index < rosterIds.length; index += 10) {
      rosterChunks.push(rosterIds.slice(index, index + 10));
    }

    const snapshots = await Promise.all(
      rosterChunks.map((chunk) =>
        getDocs(query(collection(db, 'rosters'), where('__name__', 'in', chunk)))
      )
    );

    return snapshots.flatMap((snapshot) =>
      snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
    );
  };

  const mainEventerStatus = useMemo(
    () => getFactionMainEventerStatus({ faction: factionForRoster, roster: selectedRoster }),
    [factionForRoster, selectedRoster]
  );

  const favoriteRosterId = useMemo(() => {
    if (!userProfile?.favoriteFactionId) return null;

    const favoriteEntry = Object.entries(userFactionsMap).find(
      ([, faction]) => faction?.id === userProfile.favoriteFactionId
    );

    return favoriteEntry?.[0] || null;
  }, [userFactionsMap, userProfile?.favoriteFactionId]);

  const userFactionCount = useMemo(() => {
    const factionIds = new Set([
      ...(Array.isArray(userProfile?.factionIds) ? userProfile.factionIds : []),
      ...Object.values(userFactionsMap)
        .map(faction => faction?.id)
        .filter(Boolean)
    ]);

    return factionIds.size;
  }, [userFactionsMap, userProfile?.factionIds]);

  const favoriteFactionName = useMemo(() => {
    if (!userProfile?.favoriteFactionId) return '';

    return (
      Object.values(userFactionsMap).find(
        faction => faction?.id === userProfile.favoriteFactionId
      )?.name || ''
    );
  }, [userFactionsMap, userProfile?.favoriteFactionId]);

  const selectedRosterRequiresLineup = useMemo(
    () => rosterRequiresLineup(selectedRoster, factionForRoster),
    [factionForRoster, selectedRoster]
  );

  const expiredMainEventerStatusByRosterId = useMemo(
    () =>
      rostersParticipating.reduce((acc, roster) => {
        const faction = userFactionsMap[roster.id];
        const status = getFactionMainEventerStatus({ faction, roster });

        if (status?.kind === 'expired') {
          acc[roster.id] = status;
        }

        return acc;
      }, {}),
    [rostersParticipating, userFactionsMap]
  );

  const lineupNeedsSetup = useMemo(() => {
    if (!factionForRoster || !selectedRosterRequiresLineup) return false;

    return shouldBlockFactionScoringForMissingLineup({
      faction: factionForRoster,
      roster: selectedRoster
    });
  }, [factionForRoster, selectedRoster, selectedRosterRequiresLineup]);
  const lineupNeedsSetupByRosterId = useMemo(
    () =>
      rostersParticipating.reduce((acc, roster) => {
        const faction = userFactionsMap[roster.id];
        if (!faction || !rosterRequiresLineup(roster, faction)) {
          return acc;
        }

        if (
          shouldBlockFactionScoringForMissingLineup({
            faction,
            roster
          })
        ) {
          acc[roster.id] = true;
        }

        return acc;
      }, {}),
    [rostersParticipating, userFactionsMap]
  );
  const marketPendingRequestsCount = useMemo(
    () => pendingRequestsByRosterId[selectedRosterId] || 0,
    [pendingRequestsByRosterId, selectedRosterId]
  );
  const hasAnyFactionAlerts = useMemo(
    () => Object.keys(factionAlertStateByRosterId).length > 0,
    [factionAlertStateByRosterId]
  );
  const factionLastShowSummary = useMemo(
    () =>
      buildFactionLastShowSummary({
        faction: factionForRoster,
        members,
        participations: latestShowEntry?.participations || [],
        show: latestShowEntry?.show || null,
        showEntries: latestShowEntry?.showEntries || []
      }),
    [factionForRoster, latestShowEntry, members]
  );

  const isFactionCreationBannerActive = !isFactionCreationSeasonOpen();
  const canCurrentUserCreateFaction = canCreateFaction(userProfile?.email);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // ---------- FUNZIONI DI CARICAMENTO DATI ----------

  // Carica i roster a partire dall'array rosterIds dell'utente
  const loadUserRosters = async (uid, providedUserData = null) => {
    try {
      // Ottieni il documento utente per leggere rosterIds
      const userData = providedUserData || (await getDoc(doc(db, 'users', uid))).data();
      if (!userData) return [];
      const rosterIdsFromUser = Array.isArray(userData.rosterIds) ? userData.rosterIds : [];
      const factionIds = buildUserFactionIds(userData);
      const factions = await fetchFactionsByIds(factionIds);
      const rosterIdsFromFactions = factions
        .map((faction) => faction?.rosterId)
        .filter(Boolean);

      let participantRosters = [];
      if (rosterIdsFromUser.length === 0 && rosterIdsFromFactions.length === 0) {
        const participantSnapshot = await getDocs(
          query(collection(db, 'rosters'), where('participantIds', 'array-contains', uid))
        );
        participantRosters = participantSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
      }

      const rosterIds = Array.from(
        new Set([
          ...rosterIdsFromUser,
          ...rosterIdsFromFactions,
          ...participantRosters.map((roster) => roster.id)
        ])
      );

      if (rosterIds.length === 0) {
        setRostersParticipating([]);
        setSelectedRosterId(null);
        setSelectedRoster(null);
        setFactionForRoster(null);
        return [];
      }

      // Ottieni i dettagli di tutti i roster con un solo getDocs usando 'in'
      // Nota: Firestore supporta fino a 10 valori in una clausola 'in'
      // Se l'array è più lungo, dovresti suddividere in batch o usare getDoc singoli
      const fetchedRosters = await fetchRostersByIds(rosterIds);
      const rostersMap = new Map(
        [...participantRosters, ...fetchedRosters].map((roster) => [roster.id, roster])
      );
      const rosters = rosterIds.map((rosterId) => rostersMap.get(rosterId)).filter(Boolean);
      setRostersParticipating(rosters);

      // Seleziona il primo roster se nessuno è già selezionato
      if (rosters.length > 0 && !selectedRosterId) {
        setSelectedRosterId(rosters[0].id);
      } else if (rosters.length === 0) {
        setSelectedRosterId(null);
        setSelectedRoster(null);
        setFactionForRoster(null);
      }

      return rosters;
    } catch (err) {
      console.error('Errore caricamento roster partecipanti:', err);
      return [];
    }
  };

  // Carica le fazioni a partire dall'array factionIds dell'utente
  const loadUserFactionsMap = async (uid, providedUserData = null) => {
    try {
      // Leggi il profilo utente per ottenere factionIds
      const userData = providedUserData || (await getDoc(doc(db, 'users', uid))).data();
      if (!userData) return {};
      const factionIds = buildUserFactionIds(userData);
      let factions = [];

      if (factionIds.length > 0) {
        factions = await fetchFactionsByIds(factionIds);
      } else {
        const ownedFactionsSnapshot = await getDocs(
          query(collection(db, 'factions'), where('ownerUid', '==', uid))
        );
        factions = ownedFactionsSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
      }

      if (factions.length === 0) {
        setUserFactionsMap({});
        return {};
      }

      const map = {};
      factions.forEach((faction) => {
        if (faction.rosterId) {
          map[faction.rosterId] = faction;
        } else {
          console.warn('Faction without rosterId:', faction);
        }
      });
      setUserFactionsMap(map);
      return map;
    } catch (err) {
      console.error('Errore caricamento fazioni utente:', err);
      return {};
    }
  };

  const loadFactionWrestlers = async (faction, rosterForScore = selectedRoster, gmMatchesForScore = []) => {
    try {
      const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
      const wrestlersMap = {};
      wrestlersSnap.forEach(wDoc => {
        const wData = wDoc.data();
        wrestlersMap[wDoc.id] = {
          ...wData,
          isInjured: wData.isInjured ?? wData.is_injured ?? wData.injured ?? false,
          imageUrl: wData.imageUrl || '/assets/default-wrestler.png'
        };
      });

      const detailedMembers = mergeFactionWrestlersWithDetails(faction, wrestlersMap);

      const totalScore = rosterForScore?.isGM
        ? (calculateGmMatchPointsByFaction(gmMatchesForScore)[faction.id] || 0)
        : calculateFactionTotalScore({ faction, roster: rosterForScore, members: detailedMembers });


      setFactionForRoster({
        ...faction,
        totalScore,
        isGM: Boolean(rosterForScore?.isGM),
        creditsLeft: faction.creditsLeft || 0,
        createdAt: faction.createdAt
      });
      setMembers(detailedMembers);
    } catch (err) {
      console.error('Errore caricando wrestler della fazione:', err);
    }
  };

  const loadRosterDetails = async (rosterId) => {
    if (!rosterId) {
      setSelectedRoster(null);
      setFactionForRoster(null);
      setMembers([]);
      return;
    }

    try {
      const rosterDoc = await getDoc(doc(db, 'rosters', rosterId));
      if (!rosterDoc.exists()) return;
      const rosterData = { id: rosterDoc.id, ...rosterDoc.data() };
      setSelectedRoster(rosterData);

      const faction = userFactionsMap[rosterId];
      if (faction) {
        const factionMatches = await loadFactionGmMatches(rosterId, faction.id);
        await loadFactionWrestlers(faction, rosterData, factionMatches);
      } else {
        setFactionForRoster(null);
        setMembers([]);
      }
    } catch (err) {
      console.error('Errore caricamento dettagli roster/fazione:', err);
    }
  };

  const loadFactionGmMatches = async (rosterId, factionId) => {
    if (!rosterId || !factionId) {
      return [];
    }

    try {
      const matchesSnap = await getDocs(collection(db, 'rosters', rosterId, 'matches'));
      const normalizedMatches = matchesSnap.docs.map(docSnap => normalizeGmMatch(docSnap));
      const { matches } = await autoCloseExpiredGmMatches({
        db,
        rosterId,
        matches: normalizedMatches,
        persist: true
      });
      return getFactionGmMatches(matches, factionId);
    } catch (err) {
      console.error('Errore caricamento match GM della fazione:', err);
      return [];
    }
  };

  // ---------- GESTORI ----------

  const handleEditFaction = (factionId) => {
    setIsEditingFaction(true);
    setEditingFactionId(factionId);
  };

  const handleSaveFaction = async (updatedFaction) => {
    const favoritePreferenceTouched = Boolean(updatedFaction._favoritePreferenceTouched);

    // Aggiorna la mappa delle fazioni localmente (in attesa di ricaricare)
    setUserFactionsMap(prev => ({
      ...prev,
      [updatedFaction.rosterId]: { ...prev[updatedFaction.rosterId], ...updatedFaction }
    }));
    let nextFavoriteId = userProfile?.favoriteFactionId || null;
    setUserProfile(prev => {
      if (!prev) return prev;

      const currentFavoriteId = prev.favoriteFactionId || null;
      nextFavoriteId = updatedFaction._favoriteUpdated
        ? updatedFaction.id
        : currentFavoriteId === updatedFaction.id
          ? null
          : currentFavoriteId;

      return {
        ...prev,
        favoriteFactionId: nextFavoriteId
      };
    });

    if (favoritePreferenceTouched && userProfile?.uid) {
      if (updatedFaction._favoriteUpdated) {
        saveFavoriteFactionSelection({
          uid: userProfile.uid,
          favoriteFactionId: updatedFaction.id,
          faction: updatedFaction
        });
      } else {
        clearFavoriteFactionSelection(userProfile.uid);
      }
    } else if (userProfile?.uid && nextFavoriteId === updatedFaction.id) {
      saveFavoriteFactionSelection({
        uid: userProfile.uid,
        favoriteFactionId: updatedFaction.id,
        faction: updatedFaction
      });
    }

    if (selectedRosterId === updatedFaction.rosterId) {
      setFactionForRoster(prev => ({ ...prev, ...updatedFaction }));
    }
    setIsEditingFaction(false);
    setEditingFactionId(null);
    setShowFactionFlow(false);
    setSkipFactionModal(false);
    // Ricarica i dati per avere la versione più recente
    await loadRosterDetails(selectedRosterId);
  };

  const handleSaveProfile = async (updatedData) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Utente non autenticato');

      await updateDoc(doc(db, 'users', user.uid), {
        username: updatedData.username,
        profileImageUrl: updatedData.profileImageUrl,
      });

      // Aggiorna gli stati locali
      setUserProfile(prev => ({ ...prev, ...updatedData }));
      setFormDataProfile(prev => ({ ...prev, ...updatedData }));
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Errore salvataggio profilo:', error);
      setError('Errore durante il salvataggio del profilo');
    }
  };

  const handleCancelEdit = () => {
    setIsEditingFaction(false);
    setEditingFactionId(null);
    setShowFactionFlow(false);
    setSkipFactionModal(false);
  };

  const handleCreateNewFaction = () => {
    if (!canCurrentUserCreateFaction) return;

    setEditingFactionId(null);
    setSkipFactionModal(false);
    setDefaultRoster(null);
    setShowFactionFlow(true);
    setIsDrawerOpen(false);
  };

  const handleCreateFactionForCurrentRoster = () => {
    if (!canCurrentUserCreateFaction) return;

    setEditingFactionId(null);
    setSkipFactionModal(true);
    setDefaultRoster(selectedRoster);
    setShowFactionFlow(true);
  };

  const handleFactionCreated = async () => {
    if (userProfile?.uid) {
      await loadUserFactionsMap(userProfile.uid);
      if (selectedRosterId) {
        await loadRosterDetails(selectedRosterId);
      }
    }
    setShowFactionFlow(false);
    setDefaultRoster(null);
  };

  // ---------- EFFECTS ----------
  // ---------- EFFECTS ----------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setError(null);
        setProfileBootstrapReady(false);
        setLoading(false);
        navigate('/auth', { replace: true });
        return;
      }

      let shouldWaitForRosterContext = false;

      try {
        setLoading(true);
        setProfileBootstrapReady(false);
        setIsRosterContextLoading(false);
        setError(null);

        // Profilo utente
        const uSnap = await getDoc(doc(db, 'users', user.uid));
        if (!uSnap.exists()) throw new Error("Profilo utente non trovato");
        const uData = uSnap.data();
        const resolvedIdentity = buildProfileIdentity(user, uData);
        const normalizedFactionIds = buildUserFactionIds(uData);
        setUserProfile({
          uid: user.uid,
          ...uData,
          ...resolvedIdentity,
          factionIds: normalizedFactionIds
        });
        setFormDataProfile(resolvedIdentity);

        // Carica roster partecipanti (basati su rosterIds) e mappa fazioni
        const rosters = await loadUserRosters(user.uid, uData);
        const factionsMap = await loadUserFactionsMap(user.uid, uData);
        const favoriteRosterEntry = uData.favoriteFactionId
          ? Object.entries(factionsMap).find(([, faction]) => faction?.id === uData.favoriteFactionId)
          : null;
        const participatingRosterIds = new Set(rosters.map(roster => roster.id));
        const initialRosterId = favoriteRosterEntry?.[0] && participatingRosterIds.has(favoriteRosterEntry[0])
          ? favoriteRosterEntry[0]
          : rosters[0]?.id || null;

        setSelectedRosterId(initialRosterId);
        setProfileBootstrapReady(true);
        shouldWaitForRosterContext = Boolean(initialRosterId);

        if (!initialRosterId) {
          setSelectedRoster(null);
          setFactionForRoster(null);
          setMembers([]);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Errore nel caricamento del profilo");
      } finally {
        if (!shouldWaitForRosterContext) {
          setLoading(false);
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!profileBootstrapReady) return;

    if (!selectedRosterId) {
      setSelectedRoster(null);
      setFactionForRoster(null);
      setMembers([]);
      setIsRosterContextLoading(false);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const hydrateRosterContext = async () => {
      if (!loadingRef.current) {
        setIsRosterContextLoading(true);
      }

      try {
        await loadRosterDetails(selectedRosterId);
      } finally {
        if (!isMounted) return;
        setIsRosterContextLoading(false);
        setLoading(false);
      }
    };

    hydrateRosterContext();

    return () => {
      isMounted = false;
    };
  }, [profileBootstrapReady, selectedRosterId, userFactionsMap]);

  useEffect(() => {
    if (!profileBootstrapReady) return;

    let isMounted = true;

    const loadLatestShowEntry = async () => {
      try {
        const entry = await findLatestScoredShowEntry({ db });
        if (isMounted) {
          setLatestShowEntry(entry);
        }
      } catch (err) {
        console.error('Errore caricamento ultimo show profilo:', err);
        if (isMounted) {
          setLatestShowEntry(null);
        }
      }
    };

    loadLatestShowEntry();

    return () => {
      isMounted = false;
    };
  }, [profileBootstrapReady]);

  useEffect(() => {
    if (rostersParticipating.length === 0) {
      setPendingRequestsByRosterId({});
      return;
    }

    let isMounted = true;

    const loadPendingMarketRequests = async () => {
      try {
        const rosterCounts = await Promise.all(
          rostersParticipating.map(async roster => {
            const faction = userFactionsMap[roster.id];
            if (!faction?.id) {
              return [roster.id, 0];
            }

            const requestsSnap = await getDocs(
              collection(db, 'rosters', roster.id, 'swapRequest')
            );

            const pendingCount = requestsSnap.docs.filter(docSnap => {
              const requestData = docSnap.data();
              return (
                requestData?.status === 'pending' &&
                requestData?.targetFactionId === faction.id
              );
            }).length;

            return [roster.id, pendingCount];
          })
        );

        if (!isMounted) return;

        setPendingRequestsByRosterId(Object.fromEntries(rosterCounts));
      } catch (err) {
        console.error('Errore caricamento richieste mercato:', err);
        if (isMounted) {
          setPendingRequestsByRosterId({});
        }
      }
    };

    loadPendingMarketRequests();

    return () => {
      isMounted = false;
    };
  }, [rostersParticipating, userFactionsMap]);

  useEffect(() => {
    if (!userProfile?.uid) {
      setFactionAlertStateByRosterId({});
      return;
    }

    let isMounted = true;

    const syncFactionAlerts = async () => {
      try {
        const alertState = await loadUserFactionAlertState({
          db,
          userId: userProfile.uid
        });

        if (!isMounted) return;

        setFactionAlertStateByRosterId(alertState.alertsByRosterId || {});
      } catch (err) {
        console.error('Errore caricamento alert fazioni profilo:', err);
        if (isMounted) {
          setFactionAlertStateByRosterId({});
        }
      }
    };

    syncFactionAlerts();

    return () => {
      isMounted = false;
    };
  }, [factionForRoster?.id, members, rostersParticipating, userFactionsMap, userProfile?.uid]);

  useEffect(() => {
    if (!userProfile?.uid || !favoriteRosterId) return;

    const favoriteKey = `${userProfile.uid}:${userProfile.favoriteFactionId}`;
    if (appliedFavoriteDefaultRef.current === favoriteKey) return;

    appliedFavoriteDefaultRef.current = favoriteKey;
    setSelectedRosterId(favoriteRosterId);
  }, [favoriteRosterId, userProfile?.favoriteFactionId, userProfile?.uid]);

  // ---------- RENDER ----------
  if (loading) return <LoadingSpinner fullPage text="Carichiamo profilo, roster e fazioni" />;
  if (error) return (
    <div className="container py-5">
      <div className="alert alert-danger">{error}</div>
    </div>
  );

  return (
    <div className="profile-page-shell">
      <div className="container py-4 py-lg-5">
        <SeasonCreationBanner userEmail={userProfile?.email} className="mb-4" />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="brand-title mb-0">Profilo</h3>
          <button
            className={`btn btn-sm btn-outline-brand-secondary rounded-pill d-flex align-items-center justify-content-start text-start profile-roster-trigger ${hasAnyFactionAlerts ? 'profile-roster-trigger--has-alerts' : ''}`}
            onClick={() => setIsDrawerOpen(true)}
            title="Visualizza roster e fazioni"
            style={{
              width: 'auto',
              height: 'auto',
            }}
          >
            {hasAnyFactionAlerts && (
              <span
                className="app-attention-dot app-attention-dot--button"
                aria-hidden="true"
              />
            )}
            <span className="profile-roster-trigger__icon" aria-hidden="true">
              <i className="bi bi-list" style={{ fontSize: '1rem' }}></i>
            </span>
            <span className="profile-roster-trigger__body">
              <span className="profile-roster-trigger__label">Roster e fazioni</span>
              <span className="profile-roster-trigger__hint">
                <AnimatedNumber value={rostersParticipating.length} />
                <span>
                  {rostersParticipating.length === 1 ? ' fazione' : ' fazioni'}
                </span>
              </span>
            </span>
            <i
              className="bi bi-chevron-right profile-roster-trigger__arrow"
              aria-hidden="true"
            ></i>
          </button>
        </div>

        <div className="row align-items-start">
          <div className="col-12">
            <div className="profile-stack">
              {isEditingProfile ? (
                <EditProfileForm
                  formData={formDataProfile}
                  setFormData={setFormDataProfile}
                  setEditProfile={setIsEditingProfile}
                  handleSaveProfile={handleSaveProfile}
                />
              ) : (
                <UserProfileCard
                  userId={userProfile?.uid}
                  username={userProfile?.username}
                  email={userProfile?.email}
                  profileImageUrl={userProfile?.profileImageUrl}
                  rosterCount={rostersParticipating.length}
                  factionCount={userFactionCount}
                  favoriteFactionName={favoriteFactionName}
                  onEdit={() => setIsEditingProfile(true)}
                />
              )}
            </div>
          </div>

          {!isRosterContextLoading && factionForRoster && (
            <div className="profile-quick-actions mt-3">
              {lineupNeedsSetup && (
                <div className="alert alert-warning d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-0"
                  style={{
                    backgroundColor: '#28a74510',   // verde con 6% opacità
                    borderColor: 'var(--brand-secondary',
                    color: 'rgba(255,255,255,0.8)'
                  }}>
                  <div>
                    <strong className="d-block mb-1">Line-up da impostare</strong>
                    <span>
                      Questo roster richiede una line-up. Se non la imposti, la fazione non potrà ottenere punti.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-brand-secondary"
                    onClick={() => navigate(`/factions/${factionForRoster.id}/lineup`)}
                  >
                    <i className="bi bi-grid-3x3-gap-fill me-1"></i>
                    Imposta line-up
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="col-12">
            <div className="profile-main-column">

              <section className="profile-faction-stage">
                <div className="profile-panel-head">
                  <div>
                    <p className="mb-0 text-muted-brand">
                    </p>
                  </div>
                </div>

                <div className="profile-faction-content">
                  {isRosterContextLoading ? (
                    <div className="profile-empty-state compact">
                      <LoadingSpinner text="Carichiamo i dettagli del roster" />
                    </div>
                  ) : factionForRoster ? (
                    <FactionCard
                      faction={factionForRoster}
                      members={members}
                      onEdit={() => handleEditFaction(factionForRoster.id)}
                      isEditing={isEditingFaction && editingFactionId === factionForRoster.id}
                      onSave={handleSaveFaction}
                      onCancel={handleCancelEdit}
                      formData={formDataFaction}
                      setFormData={setFormDataFaction}
                      isFavorite={userProfile?.favoriteFactionId === factionForRoster.id}
                      userProfile={userProfile}
                      lastShowSummary={factionLastShowSummary}
                    />
                  ) : selectedRoster && selectedRoster.gameType === 'Indipendente' ? (
                    <div className="profile-empty-state">
                      <i className="bi bi-people display-1 text-brand-secondary mb-3 opacity-50"></i>
                      <h5 className="mb-3 brand-title">Non hai ancora una fazione in questo roster</h5>
                      <p className="text-muted-brand mb-4">
                        Crea la tua fazione per iniziare a giocare in "{selectedRoster.name}"
                      </p>
                      <button className="btn btn-brand-secondary btn-lg" onClick={handleCreateFactionForCurrentRoster} disabled={!canCurrentUserCreateFaction}>
                        <i className="bi bi-plus-circle me-2"></i>
                        Scegli i wrestler
                      </button>
                    </div>
                  ) : selectedRoster ? (
                    <div className="profile-empty-state compact">
                      <i className="bi bi-exclamation-triangle text-brand-secondary fs-1 mb-3 d-block"></i>
                      <p className="text-muted-brand mb-0">Fazione non presente. Aspetta che l'admin del roster crei la tua fazione</p>
                    </div>
                  ) : (
                    <div className="profile-empty-state">
                      <i className="bi bi-collection display-1 text-brand-secondary mb-3 opacity-50"></i>
                      <h5 className="mb-3 brand-title">Nessuna fazione</h5>
                      <p className="text-muted-brand mb-4">
                        Crea una nuova fazione dal menù
                      </p>
                      <button className="btn btn-outline-brand-secondary" onClick={() => setIsDrawerOpen(true)}>
                        <i className="bi bi-grid-3x3-gap me-2"></i>
                        Apri menù
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {!isRosterContextLoading && factionForRoster && (
                <div className="profile-quick-actions">

                  <button
                    type="button"
                    className="profile-action-tile"
                    onClick={() => navigate(`/factions/${factionForRoster.id}/market`)}
                  >
                    {marketPendingRequestsCount > 0 && (
                      <span className="profile-action-count">
                        {marketPendingRequestsCount}
                      </span>
                    )}
                    <div>
                      <h6 className="mb-1 brand-title text-start">
                        <i className="bi bi-shop-window me-2 text-brand-secondary"></i>
                        Mercato
                      </h6>
                      <p className="mb-0 text-muted-brand">Acquista, svincola e scambia wrestler</p>
                    </div>
                    <i className="bi bi-arrow-right"></i>
                  </button>

                  {selectedRosterRequiresLineup && (
                    <button
                      type="button"
                      className="profile-action-tile"
                      onClick={() => navigate(`/factions/${factionForRoster.id}/lineup`)}
                    >
                      <div>
                        <h6 className="mb-1 brand-title text-start">
                          <i className="bi bi-grid-3x3-gap-fill me-2 text-brand-secondary"></i>
                          Line-up
                        </h6>
                        {lineupNeedsSetup ? (
                          <span className="profile-alert-badge">Line-up da impostare!</span>
                        ) : mainEventerStatus ? (
                          <span className="profile-alert-badge">{mainEventerStatus.label}</span>
                        ) : (
                          <p className="mb-0 text-muted-brand">Gestisci i ruoli dei tuoi wrestler</p>
                        )}
                      </div>
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  )}

                  {selectedRoster?.isGM && (
                    <button
                      type="button"
                      className="profile-action-tile"
                      onClick={() => navigate(`/factions/${factionForRoster.id}/matches`)}
                    >
                      <div>
                        <h6 className="mb-1 brand-title text-start">
                          <i className="bi bi-controller me-2 text-brand-secondary"></i>
                          Match GM
                        </h6>
                        <p className="mb-0 text-muted-brand">Storico, risultati e punti wrestler</p>
                      </div>
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  )}
                </div>
              )}

              {!isRosterContextLoading && selectedRoster && selectedRoster.ownerUid === userProfile?.uid && (
                <section className="">
                  <div className="profile-admin-cta">
                    <button
                      type="button"
                      className="profile-action-tile"
                      onClick={() => navigate(`/roster/manage/${selectedRoster.id}`)}
                    >
                      <div className='text-start'>
                        <h6 className="mb-1 brand-title">
                          <i className="bi bi-diagram-3 me-2 text-brand-secondary"></i>
                          Gestisci il tuo roster
                        </h6>
                        <p className="mb-0 text-muted-brand">
                          Accedi all’area di gestione per partecipanti e impostazioni del roster.
                        </p>
                      </div>
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
      <SectionAd placement="profile" />

      {/* Offcanvas (tendina laterale) con la lista dei roster */}
      <Offcanvas
        show={isDrawerOpen}
        onHide={() => setIsDrawerOpen(false)}
        placement="end"
        className="brand-offcanvas"
      >
        <Offcanvas.Header closeButton className="border-bottom border-secondary bg-brand-primary">
          <Offcanvas.Title className="brand-title">
            <i className="bi bi-grid-3x3-gap-fill me-2"></i>
            I tuoi roster
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="bg-dark bg-opacity-90 profile-offcanvas-body d-flex flex-column">
          {rostersParticipating.length === 0 ? (
            <p className="text-muted-brand text-center">Non fai parte di nessun roster</p>
          ) : (
            <>
              <div className="list-group profile-drawer-list">
                {rostersParticipating.map(roster => {
                  const faction = userFactionsMap[roster.id];
                  const hasFaction = !!faction;
                  const isFavoriteFaction = userProfile?.favoriteFactionId === faction?.id;
                  const expiredMainEventerStatus = expiredMainEventerStatusByRosterId[roster.id];
                  const hasPendingRequests = (pendingRequestsByRosterId[roster.id] || 0) > 0;
                  const factionAlertState = factionAlertStateByRosterId[roster.id] || null;
                  const lineupNeedsSetupForRoster =
                    Boolean(lineupNeedsSetupByRosterId[roster.id]) ||
                    Boolean(factionAlertState?.hasLineupAlert);
                  const mainEventerAlertStatus =
                    factionAlertState?.mainEventerStatus || expiredMainEventerStatus || null;
                  const hasInjuryAlert = Boolean(factionAlertState?.hasInjuryAlert);
                  return (
                    <button
                      key={roster.id}
                      className={`list-group-item list-group-item-action profile-drawer-item ${selectedRosterId === roster.id ? 'is-selected' : ''
                        } ${isFavoriteFaction ? 'is-favorite' : ''}`}
                      onClick={() => {
                        setSelectedRosterId(roster.id);
                        setIsDrawerOpen(false);
                      }}
                      style={{
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                      }}
                    >
                      {hasPendingRequests && (
                        <span className="profile-drawer-item__notification bg-brand-secondary text-brand-primary">
                          {pendingRequestsByRosterId[roster.id]}
                        </span>
                      )}
                      {!hasPendingRequests && factionAlertState?.hasAnyAlert && (
                        <span
                          className="app-attention-dot app-attention-dot--drawer"
                          aria-hidden="true"
                        />
                      )}
                      <div className="d-flex flex-column align-items-start text-start">
                        <strong className={`profile-drawer-item__title ${selectedRosterId === roster.id ? 'text-brand-secondary' : 'text-white'}`}>
                          {roster.name}
                        </strong>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {hasFaction ? (
                            <>
                              <span className={`badge small px-2 py-1 rounded-pill ${selectedRosterId === roster.id ? 'bg-brand-secondary text-brand-primary' : 'bg-secondary bg-opacity-25 text-muted-brand'}`}>
                                {faction.name}
                              </span>
                              {isFavoriteFaction && (
                                <span className="badge bg-warning bg-opacity-25 text-warning small px-2 py-1 rounded-pill">
                                  <i className="bi bi-star-fill me-1"></i>
                                  Preferita
                                </span>
                              )}
                              {lineupNeedsSetupForRoster && (
                                <span className="profile-lineup-warning-badge">
                                  <i className="bi bi-grid-3x3-gap-fill me-1"></i>
                                  Line-up da impostare
                                </span>
                              )}
                              {mainEventerAlertStatus && (
                                <span className="profile-alert-badge">
                                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                  {mainEventerAlertStatus.label}
                                </span>
                              )}
                              {hasInjuryAlert && (
                                <span className="profile-faction-attention-badge">
                                  <i className="bi bi-heart-pulse-fill me-1"></i>
                                  Infortuni
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="badge bg-secondary bg-opacity-25 text-muted-brand small px-2 py-1 rounded-pill">
                              Nessuna fazione
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Spazio flessibile per spingere il pulsante in basso */}
              <div className="flex-grow-1"></div>

            </>
          )}
          {/* Pulsante in basso a destra con rilievo */}
          <div className="d-flex justify-content-end mt-4 pt-2">
            <button
              className="btn btn-brand-secondary rounded-pill px-4 py-2 fw-semibold shadow-lg w-100"
              onClick={handleCreateNewFaction}
              disabled={!canCurrentUserCreateFaction}
              style={{
                transition: 'transform 0.2s, box-shadow 0.2s',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
              }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Nuova fazione
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Modal per creare/modificare fazione */}
      {showFactionFlow && (
        <CreateFactionFlow
          userProfile={userProfile}
          skipInitialStep={skipFactionModal}
          existingFactionId={editingFactionId}
          defaultRoster={defaultRoster}
          onClose={() => {
            setShowFactionFlow(false);
            setSkipFactionModal(false);
            setEditingFactionId(null);
            setDefaultRoster(null);
          }}
          onRoadTo={() => { }}
          onFactionCreated={handleFactionCreated}
        />
      )}
    </div>
  );
}
